import type { GivenVsExpensesBar } from '../../../utils/employerMoneyGivenSettlement';
import {
  computeGivenVsExpensesChartBarHeightPx,
  computeGivenVsExpensesChartDisplayMax,
  computeGivenVsExpensesChartDisplayMid,
  computeGivenVsExpensesChartValuePx,
  formatChartAxisAmount,
  formatEmployerMoneyAmount,
} from '../../../utils/employerMoneyGivenSettlement';
import { PressableHint } from '../../ui/PressableHint';
import { PressableHintProvider } from '../../ui/PressableHintContext';

type MoneyGivenVsExpensesChartProps = {
  bars: GivenVsExpensesBar[];
  givenLabel: string;
  expensesLabel: string;
  pendingExpensesLabel: string;
  title: string;
  subtitle: string;
  loading?: boolean;
  emptyLabel?: string;
};

type ChartBarHintProps = {
  hintId: string;
  detail: string;
  barClassName: string;
  barHeightPx: number;
};

const CHART_DRAWABLE_HEIGHT_PX = 96;
const CHART_TOOLTIP_PADDING_PX = 18;
const CHART_PLOT_HEIGHT_PX = CHART_DRAWABLE_HEIGHT_PX + CHART_TOOLTIP_PADDING_PX;
/** Narrow column for $-prefixed axis ticks (e.g. $1400). */
const CHART_Y_AXIS_WIDTH = '1.75rem';
/** Height reserved below the plot for -45° period labels so they do not overlap bars. */
const ROTATED_PERIOD_LABEL_ROW_HEIGHT_PX = 50;
/** Vertical offset within the label row — keeps -45° text low and clear of bars. */
const ROTATED_PERIOD_LABEL_TOP_OFFSET_PX = 10;
/** Nudge -45° labels left so the period en-dash sits between paired bars (rotation shifts visual center). */
const ROTATED_PERIOD_LABEL_LEFT_OFFSET_PX = 8;
/** Period labels longer than this (e.g. MM-DD – MM-DD) rotate on the X-axis. */
const LONG_PERIOD_LABEL_THRESHOLD = 9;

const isLongPeriodLabel = (label: string): boolean => label.length > LONG_PERIOD_LABEL_THRESHOLD;

const gridLineTopPx = (lineBottomPx: number, drawableHeightPx: number): number =>
  drawableHeightPx - lineBottomPx;

const ChartBarHint = ({ hintId, detail, barClassName, barHeightPx }: ChartBarHintProps) => (
  <div className="flex w-1/3 min-w-[10px] flex-col justify-end self-end">
    <PressableHint
      hintId={hintId}
      detail={detail}
      ariaLabel={detail}
      placement="top"
      rootClassName="relative w-full self-end"
      triggerClassName="!flex w-full !items-end justify-center border-0 bg-transparent p-0"
    >
      <span
        className={`block w-full min-w-[10px] rounded-t-sm ${barClassName}`}
        style={{ height: `${barHeightPx}px`, minHeight: barHeightPx > 0 ? '4px' : '0px' }}
        aria-hidden
      />
    </PressableHint>
  </div>
);

export const MoneyGivenVsExpensesChart = ({
  bars,
  givenLabel,
  expensesLabel,
  pendingExpensesLabel,
  title,
  subtitle,
  loading = false,
  emptyLabel,
}: MoneyGivenVsExpensesChartProps) => {
  const showOrangeLegend = bars.some((bar) => bar.expensesTone === 'orange');
  const maxGiven = Math.max(...bars.map((bar) => bar.givenAmount), 0);
  const maxExpenses = Math.max(...bars.map((bar) => bar.expensesAmount ?? 0), 0);
  const rawChartMax = Math.max(maxGiven, maxExpenses, 1);
  const displayMax = computeGivenVsExpensesChartDisplayMax(rawChartMax);
  const displayMid = computeGivenVsExpensesChartDisplayMid(displayMax);
  const maxGridBottomPx = computeGivenVsExpensesChartValuePx(
    displayMax,
    displayMax,
    CHART_DRAWABLE_HEIGHT_PX
  );
  const midGridBottomPx = computeGivenVsExpensesChartValuePx(
    displayMid,
    displayMax,
    CHART_DRAWABLE_HEIGHT_PX
  );
  const hasLongPeriodLabels = bars.some((bar) => isLongPeriodLabel(bar.periodLabel));

  return (
    <article className="overflow-visible rounded-2xl border border-gray-300/80 bg-white p-4 shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
      <div className="mb-4 flex flex-col gap-1.5">
        <h2 className="flex items-center gap-2 text-[15px] font-bold text-gray-900 dark:text-gray-100">
          <span className="text-primary-600" aria-hidden>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 21v-6m7 6V3m7 18V9" />
            </svg>
          </span>
          {title}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-10" role="status" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
        </div>
      ) : bars.length === 0 ? (
        emptyLabel ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</p>
        ) : null
      ) : (
        <>
          <PressableHintProvider>
            <div
              className="overflow-visible border-t border-gray-200/40 pt-4 dark:border-gray-700/40"
              role="img"
              aria-label={title}
            >
              <div>
                <div
                  className="flex gap-1.5"
                  style={{ height: `${CHART_PLOT_HEIGHT_PX}px` }}
                >
                  <div
                    className="relative shrink-0"
                    style={{ width: CHART_Y_AXIS_WIDTH, height: `${CHART_PLOT_HEIGHT_PX}px` }}
                    aria-hidden
                  >
                    <div
                      className="absolute inset-x-0 bottom-0 text-right text-[9px] font-medium tabular-nums leading-none text-gray-400 dark:text-gray-500"
                      style={{ height: `${CHART_DRAWABLE_HEIGHT_PX}px` }}
                    >
                      <span
                        className="absolute right-0 -translate-y-1/2 leading-none"
                        style={{ top: `${gridLineTopPx(maxGridBottomPx, CHART_DRAWABLE_HEIGHT_PX)}px` }}
                      >
                        {formatChartAxisAmount(displayMax)}
                      </span>
                      <span
                        className="absolute right-0 -translate-y-1/2 leading-none"
                        style={{ top: `${gridLineTopPx(midGridBottomPx, CHART_DRAWABLE_HEIGHT_PX)}px` }}
                      >
                        {formatChartAxisAmount(displayMid)}
                      </span>
                    </div>
                  </div>
                  <div
                    className="relative min-w-0 flex-1 overflow-visible"
                    style={{ height: `${CHART_PLOT_HEIGHT_PX}px` }}
                  >
                    <div
                      className="absolute inset-x-0 bottom-0 overflow-visible"
                      style={{ height: `${CHART_DRAWABLE_HEIGHT_PX}px` }}
                    >
                      <div
                        className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-200/70 dark:border-gray-600/70"
                        style={{ bottom: `${maxGridBottomPx}px` }}
                        aria-hidden
                      />
                      <div
                        className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-200/70 dark:border-gray-600/70"
                        style={{ bottom: `${midGridBottomPx}px` }}
                        aria-hidden
                      />
                      <div className="flex h-full items-end justify-between gap-2 overflow-visible">
                        {bars.map((bar) => {
                          const givenBarPx = computeGivenVsExpensesChartBarHeightPx(
                            bar.givenAmount,
                            displayMax,
                            CHART_DRAWABLE_HEIGHT_PX
                          );
                          const expensesBarPx =
                            bar.expensesAmount === null
                              ? 0
                              : computeGivenVsExpensesChartBarHeightPx(
                                  bar.expensesAmount,
                                  displayMax,
                                  CHART_DRAWABLE_HEIGHT_PX
                                );
                          const expensesClass =
                            bar.expensesTone === 'orange' ? 'bg-amber-500' : 'bg-red-500';
                          const givenDetail = `${givenLabel}: ${formatEmployerMoneyAmount(bar.givenAmount)}`;
                          const expensesDetail =
                            bar.expensesAmount === null
                              ? ''
                              : `${bar.expensesTone === 'orange' ? pendingExpensesLabel : expensesLabel}: ${formatEmployerMoneyAmount(bar.expensesAmount)}`;
                          return (
                            <div
                              key={bar.budgetId}
                              className="flex min-w-0 flex-1 items-end justify-center gap-1.5 self-end"
                            >
                              <ChartBarHint
                                hintId={`given-${bar.budgetId}`}
                                detail={givenDetail}
                                barClassName="bg-emerald-500"
                                barHeightPx={givenBarPx}
                              />
                              {bar.expensesAmount !== null ? (
                                <ChartBarHint
                                  hintId={`expenses-${bar.budgetId}`}
                                  detail={expensesDetail}
                                  barClassName={expensesClass}
                                  barHeightPx={expensesBarPx}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`flex gap-1.5 ${hasLongPeriodLabels ? 'mt-5' : 'mt-3'}`}>
                  <div className="shrink-0" style={{ width: CHART_Y_AXIS_WIDTH }} aria-hidden />
                  <div
                    className="flex min-w-0 flex-1 justify-between gap-2"
                    style={
                      hasLongPeriodLabels
                        ? { minHeight: `${ROTATED_PERIOD_LABEL_ROW_HEIGHT_PX}px` }
                        : undefined
                    }
                  >
                    {bars.map((bar) => {
                      const useRotatedLabel = isLongPeriodLabel(bar.periodLabel);
                      if (useRotatedLabel) {
                        return (
                          <div
                            key={`${bar.budgetId}-label`}
                            className="relative flex min-w-0 flex-1 justify-center overflow-visible"
                            style={{ height: `${ROTATED_PERIOD_LABEL_ROW_HEIGHT_PX}px` }}
                          >
                            <span
                              className="absolute left-1/2 origin-top whitespace-nowrap text-[10px] font-bold text-gray-500 dark:text-gray-400"
                              style={{
                                top: `${ROTATED_PERIOD_LABEL_TOP_OFFSET_PX}px`,
                                transform: `translateX(calc(-50% - ${bar.expensesAmount !== null ? ROTATED_PERIOD_LABEL_LEFT_OFFSET_PX : 0}px)) rotate(-45deg)`,
                              }}
                              title={bar.periodLabel}
                            >
                              {bar.periodLabel}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <span
                          key={`${bar.budgetId}-label`}
                          className="min-w-0 flex-1 truncate px-0.5 text-center text-[10px] font-bold text-gray-500 dark:text-gray-400"
                          title={bar.periodLabel}
                        >
                          {bar.periodLabel}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </PressableHintProvider>
          <div
            className={`flex flex-wrap items-center justify-center gap-4 ${hasLongPeriodLabels ? 'mt-1' : 'mt-2'}`}
          >
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400">
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <span>{givenLabel}</span>
            </div>
            {showOrangeLegend ? (
              <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400">
                <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                <span>{pendingExpensesLabel}</span>
              </div>
            ) : null}
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
              <span>{expensesLabel}</span>
            </div>
          </div>
        </>
      )}
    </article>
  );
};
